import { useEffect, useState } from 'react';

import { useInject } from '@vk-hr-tek/core/ioc';
import { LocalStorage } from '@vk-hr-tek/core/local-storage';
import { Dialog, DialogContent } from '@vk-hr-tek/ui/Dialog';
import { Button } from '@vk-hr-tek/ui/Button';
import { Link } from '@vk-hr-tek/ui/Link';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { useIsDesktop } from '@vk-hr-tek/ui/hooks';

import { useSelector } from '@app/hooks';
import { KONTUR_RECOVERY_DEFAULT_MESSAGE } from '@app/constants';

import {
  selectIsKonturCertUnavailable,
  selectKonturSystemDownMessage,
  selectUserId,
} from '../../user';
import { KONTUR_UNAVAILABLE_DIALOG_LOCAL_STORAGE_KEY } from '../constants';

export const KonturUnavailableDialog = () => {
  const localStorage = useInject<LocalStorage>(LocalStorage);

  const isDesktop = useIsDesktop();

  const isKonturCertUnavailable = useSelector(selectIsKonturCertUnavailable);
  const userId = useSelector(selectUserId);
  const systemDownMessage = useSelector(selectKonturSystemDownMessage);

  const [isClosed, setIsClosed] = useState(false);

  const storageKey = `${KONTUR_UNAVAILABLE_DIALOG_LOCAL_STORAGE_KEY}${userId}`;

  const recoveryStatusLink = window.REACT_APP_VKHRTEK_STATUS_PAGE_LINK;
  const aboutGoskeyLink = window.REACT_APP_VKHRTEK_ABOUT_GOSKEY_LINK;

  const wasShownBefore = !!userId && !!localStorage.getItem(storageKey);

  useEffect(() => {
    if (userId && !isKonturCertUnavailable) {
      localStorage.removeItem(storageKey);
    }
  }, [localStorage, storageKey, userId, isKonturCertUnavailable]);

  const handleClose = () => {
    localStorage.setItem(storageKey, 'true');
    setIsClosed(true);
  };

  if (!isKonturCertUnavailable || !userId || wasShownBefore || isClosed) {
    return null;
  }

  return (
    <Dialog fullScreen={!isDesktop} open onClose={handleClose}>
      <DialogContent
        fullScreen={!isDesktop}
        onClose={handleClose}
        title="Электронная подпись не выпущена"
        actions={
          <Button
            onClick={handleClose}
            size="large"
            variant="tertiaryLight"
            fullWidth={!isDesktop}
          >
            Закрыть
          </Button>
        }
      >
        <Box display="flex" flexDirection="column" gap="16">
          <Box>
            <Typography
              component="span"
              variant="body2"
              color="text.light.secondary"
            >
              {`Электронная подпись `}
            </Typography>
            <Typography
              component="span"
              variant="subtitle1"
              color="text.light.secondary"
            >
              не выпущена
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color="text.light.secondary"
            >
              {` из-за недоступности сервиса выпуска подписи - СКБ Контур. Но вы можете продолжать работать без нее.`}
            </Typography>
          </Box>
          <Box>
            <Typography
              component="span"
              variant="subtitle1"
              color="text.light.secondary"
            >
              Выпуск подписи продолжится автоматически
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color="text.light.secondary"
            >
              , как только СКБ Контур станет снова доступен.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.light.secondary">
            Сейчас вы можете подписывать документы через приложение «Госключ»
            или подождать выпуска подписи от СКБ Контур. Подписи СКБ Контур или
            Госключ юридически равнозначны
          </Typography>
          <Typography variant="body2" color="text.light.tertirary">
            {systemDownMessage || KONTUR_RECOVERY_DEFAULT_MESSAGE}
          </Typography>
          {(!!recoveryStatusLink || !!aboutGoskeyLink) && (
            <Box display="flex" flexDirection="column" gap="8">
              {!!recoveryStatusLink && (
                <Link
                  size="small"
                  href={recoveryStatusLink}
                  target="_blank"
                  endIcon={<Icon name="link_outline" size="16" />}
                >
                  Статус восстановления сервиса
                </Link>
              )}
              {!!aboutGoskeyLink && (
                <Link
                  size="small"
                  href={aboutGoskeyLink}
                  target="_blank"
                  endIcon={<Icon name="link_outline" size="16" />}
                >
                  Подробнее об УНЭП Госключ
                </Link>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
